import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import Chip, {ChipSize} from 'ui-library/chips/Chip';

import type {
  CoScientistAgentWorkspaceFolderChipQuery as CoScientistAgentWorkspaceFolderChipQueryData,
  CoScientistAgentWorkspaceFolderChipQueryVariables,
} from './__generated/CoScientistAgentWorkspaceFolderChipQuery';

interface CoScientistAgentWorkspaceFolderChipProps {
  agentWorkspaceFolderId: string;
}

const CO_SCIENTIST_AGENT_WORKSPACE_FOLDER_CHIP_QUERY = gql`
  query CoScientistAgentWorkspaceFolderChipQuery($agentWorkspaceFolderId: StrictString!)
  @engComponent(name: LLM)
  @skipAudit {
    folder(id: $agentWorkspaceFolderId) {
      __typename
      ... on FolderNode {
        id
        name
      }
    }
  }
`;

/**
 * Read-only folder chip that displays the agent workspace folder name from its ID.
 * Used for existing co-scientist chats where the folder is not editable.
 */
export default function CoScientistAgentWorkspaceFolderChip({
  agentWorkspaceFolderId,
}: CoScientistAgentWorkspaceFolderChipProps): JSX.Element {
  const agentWorkspaceFolderName = useAgentWorkspaceFolderName(agentWorkspaceFolderId);

  // TODO(BNCH-147518): Replace Chip with BenchChip when BenchChip supports this usage.
  return (
    <div className={css(styles.container)}>
      <Chip
        className={css(styles.chip)}
        size={ChipSize.MINI}
        text={agentWorkspaceFolderName}
        icon={ModelTypeConstants.FOLDER}
      />
    </div>
  );
}

function useAgentWorkspaceFolderName(agentWorkspaceFolderId: string | null): string {
  const result = useSkippableBenchlingQuery<
    CoScientistAgentWorkspaceFolderChipQueryData,
    CoScientistAgentWorkspaceFolderChipQueryVariables
  >(CO_SCIENTIST_AGENT_WORKSPACE_FOLDER_CHIP_QUERY, {
    variables: {agentWorkspaceFolderId: agentWorkspaceFolderId ?? ''},
    skip: agentWorkspaceFolderId == null,
  });

  if (result.type !== QueryResultType.SUCCESS || result.data.folder?.__typename !== 'FolderNode') {
    return agentWorkspaceFolderId ?? '';
  }

  return result.data.folder.name;
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    gap: 4,
    padding: '8px 12px 0',
    overflow: 'hidden',
  },
  chip: {
    fontSize: '12px',
    whiteSpace: 'nowrap',
    marginRight: 0,
    minWidth: 0,
  },
});
